import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '../App';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { 
  ArrowLeft, 
  Users, 
  FileText, 
  ShoppingCart, 
  MessageSquare,
  Trash2,
  AlertTriangle,
  Search,
  Crown,
  Activity,
  Clock,
  Database,
  Cpu,
  RefreshCw,
  CheckCircle,
  XCircle,
  Loader2,
  Zap
} from 'lucide-react';
import { Input } from '../components/ui/input';

const Admin = () => {
  const { user } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('documents');
  const [users, setUsers] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchMetrics = useCallback(async () => {
    setMetricsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/system-metrics`);
      setMetrics(res.data);
    } catch (error) {
      toast.error('Failed to load system metrics');
    } finally {
      setMetricsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'system') {
      fetchMetrics();
    }
  }, [activeTab, fetchMetrics]);

  useEffect(() => {
    if (activeTab === 'system' && autoRefresh) {
      const interval = setInterval(fetchMetrics, 10000);
      return () => clearInterval(interval);
    }
  }, [activeTab, autoRefresh, fetchMetrics]);

  const fetchData = async () => {
    try {
      const [usersRes, docsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/documents`),
        axios.get(`${API}/admin/stats`)
      ]);
      setUsers(usersRes.data);
      setDocuments(docsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    try {
      await axios.delete(`${API}/admin/documents/${docId}`);
      toast.success('Document permanently deleted');
      setDocuments(documents.filter(d => d.id !== docId));
      setDeleteConfirm(null);
      // Refresh stats
      const statsRes = await axios.get(`${API}/admin/stats`);
      setStats(statsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete document');
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success('User and all content deleted');
      setUsers(users.filter(u => u.id !== userId));
      setDeleteConfirm(null);
      // Refresh all data
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const filteredDocuments = documents.filter(doc => 
    doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.seller_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredUsers = users.filter(u => 
    u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (u.school && u.school.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (!user?.is_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h1>
          <p className="text-gray-600 mb-4">You don't have permission to access this page.</p>
          <Link to="/">
            <Button className="rounded-xl">Go Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="outline" size="sm" className="rounded-xl">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800" style={{ fontFamily: 'Crimson Text, serif' }}>
                Admin Dashboard
              </h1>
              <p className="text-gray-600 text-sm">Manage users and content</p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-xl p-4 shadow-md border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{stats.total_users}</p>
                  <p className="text-sm text-gray-500">Users</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-md border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Crown className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{stats.total_subscribers || 0}</p>
                  <p className="text-sm text-gray-500">Subscribers</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-md border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{stats.total_documents}</p>
                  <p className="text-sm text-gray-500">Documents</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-md border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <ShoppingCart className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{stats.total_purchases}</p>
                  <p className="text-sm text-gray-500">Purchases</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-md border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{stats.total_messages}</p>
                  <p className="text-sm text-gray-500">Messages</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            onClick={() => setActiveTab('documents')}
            variant={activeTab === 'documents' ? 'default' : 'outline'}
            className="rounded-xl"
          >
            <FileText className="w-4 h-4 mr-2" />
            Documents ({documents.length})
          </Button>
          <Button
            onClick={() => setActiveTab('users')}
            variant={activeTab === 'users' ? 'default' : 'outline'}
            className="rounded-xl"
          >
            <Users className="w-4 h-4 mr-2" />
            Users ({users.length})
          </Button>
          <Button
            onClick={() => setActiveTab('system')}
            variant={activeTab === 'system' ? 'default' : 'outline'}
            className="rounded-xl"
            data-testid="admin-system-tab"
          >
            <Activity className="w-4 h-4 mr-2" />
            System
          </Button>
        </div>

        {/* Search (hidden on system tab) */}
        {activeTab !== 'system' && (
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              placeholder={`Search ${activeTab}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 rounded-xl"
            />
          </div>
        )}

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          {activeTab === 'documents' && (
            <div className="divide-y divide-gray-100">
              {filteredDocuments.length === 0 ? (
                <div className="p-8 text-center text-gray-500">No documents found</div>
              ) : (
                filteredDocuments.map((doc) => (
                  <div key={doc.id} className="p-4 hover:bg-gray-50 flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Link to={`/document/${doc.id}`} className="font-semibold text-gray-800 hover:text-blue-600 truncate">
                          {doc.title}
                        </Link>
                        {doc.deleted && (
                          <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">Deleted</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 truncate">{doc.description}</p>
                      <div className="flex items-center gap-4 mt-1 text-xs text-gray-400">
                        <span>By: {doc.seller_name}</span>
                        <span>${doc.price.toFixed(2)}</span>
                        <span>{doc.document_type}</span>
                        <span>{doc.purchase_count || 0} purchases</span>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDeleteConfirm({ type: 'document', id: doc.id, name: doc.title })}
                      className="rounded-xl border-red-300 text-red-600 hover:bg-red-50 flex-shrink-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'users' && (
            <div className="divide-y divide-gray-100">
              {filteredUsers.length === 0 ? (
                <div className="p-8 text-center text-gray-500">No users found</div>
              ) : (
                filteredUsers.map((u) => (
                  <div key={u.id} className="p-4 hover:bg-gray-50 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <Avatar className="w-10 h-10 flex-shrink-0">
                        <AvatarFallback className="bg-blue-100 text-blue-800">
                          {u.name.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <Link to={`/profile/${u.id}`} className="font-semibold text-gray-800 hover:text-blue-600 truncate">
                            {u.name}
                          </Link>
                          {u.is_admin && (
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">Admin</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 truncate">{u.email}</p>
                        <p className="text-xs text-gray-400">{u.school || 'No school'} • {u.field_of_concentration || 'No field'}</p>
                      </div>
                    </div>
                    {!u.is_admin && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeleteConfirm({ type: 'user', id: u.id, name: u.name })}
                        className="rounded-xl border-red-300 text-red-600 hover:bg-red-50 flex-shrink-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* System Metrics Tab */}
        {activeTab === 'system' && (
          <div data-testid="system-metrics-panel">
            {/* Refresh controls */}
            <div className="flex items-center gap-3 mb-6">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchMetrics}
                disabled={metricsLoading}
                className="rounded-xl"
                data-testid="refresh-metrics-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${metricsLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <button
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm border transition-colors ${
                  autoRefresh
                    ? 'bg-green-50 border-green-300 text-green-700'
                    : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                }`}
                data-testid="auto-refresh-toggle"
              >
                <div className={`w-2 h-2 rounded-full ${autoRefresh ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`} />
                Auto-refresh {autoRefresh ? 'ON' : 'OFF'}
              </button>
            </div>

            {!metrics ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Request Metrics */}
                <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-6" data-testid="request-metrics-card">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Zap className="w-5 h-5 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">Request Metrics</h3>
                    <span className="text-xs text-gray-400 ml-auto">Last 60s</span>
                  </div>
                  <div className="space-y-4">
                    <MetricRow label="Requests / min" value={metrics.request_metrics.requests_per_minute} />
                    <MetricRow label="Avg response time" value={`${metrics.request_metrics.avg_response_time_ms} ms`}
                      status={metrics.request_metrics.avg_response_time_ms > 1000 ? 'warn' : 'ok'} />
                    <MetricRow label="Slow requests (>2s)" value={metrics.request_metrics.slow_request_count}
                      status={metrics.request_metrics.slow_request_count > 0 ? 'warn' : 'ok'} />
                    <MetricRow label="5xx errors" value={metrics.request_metrics.error_count_5xx}
                      status={metrics.request_metrics.error_count_5xx > 0 ? 'error' : 'ok'} />
                  </div>
                </div>

                {/* Queue Metrics */}
                <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-6" data-testid="queue-metrics-card">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                      <Clock className="w-5 h-5 text-amber-600" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">Job Queue</h3>
                  </div>
                  <div className="space-y-4">
                    <MetricRow label="Queued" value={metrics.queue_metrics.queued}
                      status={metrics.queue_metrics.queued > 10 ? 'warn' : 'ok'} />
                    <MetricRow label="Processing" value={metrics.queue_metrics.processing} />
                    <MetricRow label="Completed" value={metrics.queue_metrics.completed} />
                    <MetricRow label="Failed" value={metrics.queue_metrics.failed}
                      status={metrics.queue_metrics.failed > 0 ? 'error' : 'ok'} />
                    <MetricRow label="Avg wait time" value={`${metrics.queue_metrics.avg_wait_time_sec}s`} />
                    <MetricRow label="Avg processing time" value={`${metrics.queue_metrics.avg_processing_time_sec}s`} />
                  </div>
                </div>

                {/* System Health */}
                <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-6" data-testid="system-health-card">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <Database className="w-5 h-5 text-green-600" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">System Health</h3>
                  </div>
                  <div className="space-y-4">
                    <MetricRow label="Uptime" value={formatUptime(metrics.system_health.uptime_seconds)} />
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">MongoDB</span>
                      <StatusBadge status={metrics.system_health.mongodb} />
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">Redis</span>
                      <StatusBadge status={metrics.system_health.redis} />
                    </div>
                  </div>
                </div>

                {/* Worker Metrics */}
                <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-6" data-testid="worker-metrics-card">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <Cpu className="w-5 h-5 text-indigo-600" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">Worker</h3>
                    <span className="text-xs text-gray-400 ml-auto">Last 60s</span>
                  </div>
                  <div className="space-y-4">
                    <MetricRow label="Jobs completed" value={metrics.worker_metrics.jobs_completed_last_60s} />
                    <div className="pt-2">
                      <p className="text-sm text-gray-600 mb-2">Currently processing</p>
                      {metrics.worker_metrics.currently_processing.length === 0 ? (
                        <p className="text-sm text-gray-400 italic">Idle</p>
                      ) : (
                        <div className="space-y-2">
                          {metrics.worker_metrics.currently_processing.map((j) => (
                            <div key={j.job_id} className="flex items-center gap-2 bg-indigo-50 rounded-lg px-3 py-2">
                              <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-500" />
                              <span className="text-sm font-medium text-indigo-700">{j.type}</span>
                              <span className="text-xs text-indigo-400 ml-auto">{j.job_id.slice(0, 8)}...</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-xl p-6 max-w-md w-full">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800">Confirm Delete</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone</p>
                </div>
              </div>
              <p className="text-gray-700 mb-6">
                Are you sure you want to permanently delete {deleteConfirm.type === 'user' ? 'user' : 'document'}{' '}
                <strong>"{deleteConfirm.name}"</strong>?
                {deleteConfirm.type === 'user' && (
                  <span className="block mt-2 text-sm text-red-600">
                    This will also delete all their documents, messages, and other content.
                  </span>
                )}
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 rounded-xl"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    if (deleteConfirm.type === 'document') {
                      handleDeleteDocument(deleteConfirm.id);
                    } else {
                      handleDeleteUser(deleteConfirm.id);
                    }
                  }}
                  className="flex-1 rounded-xl bg-red-600 hover:bg-red-700"
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// --- Helper components ---

const MetricRow = ({ label, value, status }) => (
  <div className="flex items-center justify-between py-2">
    <span className="text-sm text-gray-600">{label}</span>
    <span className={`text-sm font-semibold ${
      status === 'error' ? 'text-red-600' :
      status === 'warn' ? 'text-amber-600' :
      'text-gray-800'
    }`}>
      {value}
    </span>
  </div>
);

const StatusBadge = ({ status }) => {
  if (status === 'ok') {
    return (
      <span className="flex items-center gap-1.5 text-sm font-medium text-green-600">
        <CheckCircle className="w-4 h-4" /> OK
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span className="flex items-center gap-1.5 text-sm font-medium text-red-600">
        <XCircle className="w-4 h-4" /> Error
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1.5 text-sm font-medium text-gray-400">
      <XCircle className="w-4 h-4" /> Unavailable
    </span>
  );
};

const formatUptime = (seconds) => {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
};

export default Admin;
